import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Activity, Stethoscope, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const submitLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      nav("/app");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col w-1/2 bg-primary text-primary-foreground relative overflow-hidden dotted-bg">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-primary/70" />
        <div className="relative z-10 flex flex-col h-full p-12">
          <div className="flex items-center gap-3">
            <div className="size-11 rounded-lg bg-primary-foreground/15 backdrop-blur grid place-items-center">
              <Activity className="size-6" />
            </div>
            <div>
              <div className="font-bold text-2xl tracking-tight">Senrize Medical Flow</div>
              <div className="text-[10px] uppercase tracking-[0.25em] opacity-70">Private practice platform</div>
            </div>
          </div>

          <div className="mt-auto space-y-8">
            <div>
              <h1 className="text-5xl font-bold tracking-tight leading-[1.05]">
                A private system <br /> for every doctor.
              </h1>
              <p className="mt-5 text-base opacity-80 max-w-md leading-relaxed">
                White-label, isolated, secure. Each doctor gets their own patient portal, booking site, and admin dashboard. No shared data, no marketplace.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-5 pt-8 border-t border-primary-foreground/15">
              {[
                { icon: Stethoscope, label: "Per-doctor" },
                { icon: ShieldCheck, label: "Isolated data" },
                { icon: Activity, label: "Real-time" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="space-y-1.5">
                  <Icon className="size-5 opacity-80" />
                  <div className="text-xs font-medium opacity-90">{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md fade-up">
          <div className="lg:hidden mb-8 flex items-center gap-2.5">
            <div className="size-10 rounded-lg bg-primary text-primary-foreground grid place-items-center">
              <Activity className="size-5" />
            </div>
            <div>
              <div className="font-bold text-xl">Senrize Medical Flow</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Private practice</div>
            </div>
          </div>

          <h2 className="text-3xl font-bold tracking-tight">Sign in</h2>
          <p className="text-sm text-muted-foreground mt-1.5">
            New patients must be registered by their doctor.
            <br />
            Looking for a doctor? Visit their booking page directly (e.g. <code className="mono text-xs bg-muted px-1.5 py-0.5 rounded">/d/dr-hadi-samara</code>).
          </p>
          <form onSubmit={submitLogin} className="space-y-4 mt-7">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required data-testid="login-email" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pr-10"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                  data-testid="login-password-toggle"
                >
                  {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11" data-testid="login-submit-btn">
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          <div className="mt-7">
            <div className="text-xs text-muted-foreground">
              Don't have an account? Your doctor must register you. <br />
              Looking for a doctor? Visit their booking page (e.g.{" "}
              <code className="mono text-xs bg-muted px-1.5 py-0.5 rounded">/d/your-doctor-slug</code>).
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-border text-center">
            <Link to="/admin" className="block text-xs text-muted-foreground hover:text-foreground" data-testid="owner-access-link">
              Platform owner access →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
